import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { PrimaryButton } from './PrimaryButton';
import { FloatingLabelInput } from './FloatingLabelInput';
import { CmsModal } from './CmsModal';
import { signup, validateProInvite } from '../../lib/professional-api';
import { setAuth } from '../../lib/auth';
import { apiErrorMessage } from '../../lib/api';
import { toast } from '../../lib/toast';
import {
  sanitizeName,
  passwordStrengthError,
  STRONG_PASSWORD_MESSAGE,
} from '../../lib/validators';

type CmsKind =
  | { code: 'TERMS_CONDITIONS'; title: 'Terms of Use' }
  | { code: 'PRIVACY_POLICY'; title: 'Privacy Policy' };

export function ProJoinForm() {
  const navigate = useNavigate();
  const { token = '' } = useParams<{ token: string }>();

  // Invite-link validation: null = checking, true/false = result.
  const [inviteValid, setInviteValid] = useState<boolean | null>(null);
  const [inviteReason, setInviteReason] = useState<string | null>(null);
  const [lockedEmail, setLockedEmail] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [errors, setErrors] = useState<Record<string, boolean>>({});
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [termsError, setTermsError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [activeCms, setActiveCms] = useState<CmsKind | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setInviteValid(false);
      setInviteReason('This invite link is invalid.');
      return;
    }
    validateProInvite(token)
      .then((res) => {
        if (cancelled) return;
        setInviteValid(res.valid);
        setInviteReason(res.reason);
        // Pre-fill from the application so the pro doesn't retype their
        // details. Name is split first-token / rest; all fields stay editable.
        setFormData((f) => {
          const parts = (res.full_name ?? '').trim().split(/\s+/).filter(Boolean);
          return {
            ...f,
            email: res.email ?? f.email,
            firstName: f.firstName || (parts[0] ?? ''),
            lastName: f.lastName || parts.slice(1).join(' '),
            phone: f.phone || (res.mobile_number ?? ''),
          };
        });
        if (res.email) setLockedEmail(res.email);
      })
      .catch((err) => {
        if (cancelled) return;
        setInviteValid(false);
        setInviteReason(apiErrorMessage(err, 'Could not check this invite link.'));
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const emailValid = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
  const passwordValid = (pw: string) => passwordStrengthError(pw) === null;

  const handleSubmit = async () => {
    const newErrors: Record<string, boolean> = {
      firstName: !formData.firstName.trim(),
      lastName: !formData.lastName.trim(),
      phone: !formData.phone.trim(),
      email: !emailValid(formData.email),
      password: !passwordValid(formData.password),
      confirmPassword:
        !formData.confirmPassword ||
        formData.password !== formData.confirmPassword,
    };
    setErrors(newErrors);
    const termsMissing = !agreeToTerms;
    setTermsError(termsMissing ? 'You must accept the terms to continue.' : null);
    if (Object.values(newErrors).some(Boolean) || termsMissing) return;

    setSubmitting(true);
    try {
      const res = await signup({
        invite_token: token,
        email: formData.email.trim().toLowerCase(),
        first_name: formData.firstName.trim(),
        last_name: formData.lastName.trim(),
        mobile_number: formData.phone.trim(),
        password: formData.password,
        accept_terms: true,
      });
      setAuth(res.accessToken, res.user);
      toast.success('Account created — check your email to verify.');
      navigate('/professional/signup/success', {
        state: { email: res.user.email },
      });
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not create account.'));
    } finally {
      setSubmitting(false);
    }
  };

  if (inviteValid === null) {
    return (
      <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[14px]">
        Checking your invite link…
      </p>
    );
  }

  if (inviteValid === false) {
    return (
      <div className="w-full max-w-[420px] mx-auto flex flex-col items-center gap-5 px-4 text-center">
        <div className="size-[60px] rounded-full bg-red-50 flex items-center justify-center">
          <span className="material-icons text-red-500 text-[30px]">error</span>
        </div>
        <h1 className="font-['Playfair_Display',sans-serif] font-extrabold text-[#8e2a2f] text-[26px] leading-[34px]">
          Invite link not valid
        </h1>
        <p className="font-['DM_Sans',sans-serif] text-[#3a3335] text-[14px] leading-[22px]">
          {inviteReason ?? 'This invite link can no longer be used.'}
        </p>
        <Link
          to="/professional/signup"
          className="font-['DM_Sans',sans-serif] text-[#8e2a2f] font-bold hover:underline text-[14px]"
        >
          Apply to join instead
        </Link>
      </div>
    );
  }

  return (
    <div className="w-full flex flex-col items-center max-w-[420px] mx-auto">
      <div className="text-center mb-8">
        <h1 className="font-['Playfair_Display',sans-serif] font-extrabold text-[#8e2a2f] !text-[25px] md:!text-[34px] leading-[32px] md:leading-[48px] mb-2">
          Create your account
        </h1>
        <p className="font-['DM_Sans',sans-serif] font-normal leading-[21px] text-[#908e8e] text-[14px] tracking-[-0.1px]">
          You've been invited to join Cherry Picked — set up your account to
          begin onboarding.
        </p>
      </div>

      <div className="w-full flex flex-col gap-6">
        <div className="grid grid-cols-2 gap-4">
          <FloatingLabelInput
            label="First Name *"
            type="text"
            value={formData.firstName}
            onChange={(val) => {
              setFormData({ ...formData, firstName: sanitizeName(val) });
              setErrors({ ...errors, firstName: false });
            }}
            icon={``}
            className="w-full"
            error={errors.firstName}
            errorMessage="First name is required"
          />
          <FloatingLabelInput
            label="Last Name *"
            type="text"
            value={formData.lastName}
            onChange={(val) => {
              setFormData({ ...formData, lastName: sanitizeName(val) });
              setErrors({ ...errors, lastName: false });
            }}
            icon={``}
            className="w-full"
            error={errors.lastName}
            errorMessage="Last name is required"
          />
        </div>

        <FloatingLabelInput
          label="Phone number"
          type="tel"
          value={formData.phone}
          onChange={(val) => {
            setFormData({ ...formData, phone: val });
            setErrors({ ...errors, phone: false });
          }}
          icon={``}
          className="w-full"
          error={errors.phone}
          errorMessage="Phone number is required"
        />

        <FloatingLabelInput
          label="Email address"
          type="email"
          value={formData.email}
          onChange={(val) => {
            setFormData({ ...formData, email: val });
            setErrors({ ...errors, email: false });
          }}
          icon={``}
          disabled={!!lockedEmail}
          className={`w-full ${lockedEmail ? 'opacity-70' : ''}`}
          error={errors.email}
          errorMessage="Please enter a valid email address"
        />

        <FloatingLabelInput
          label="Create Password"
          type="password"
          value={formData.password}
          onChange={(val) => {
            setFormData({ ...formData, password: val });
            setErrors({ ...errors, password: false });
          }}
          icon={``}
          className="w-full"
          error={errors.password}
          errorMessage={STRONG_PASSWORD_MESSAGE}
        />

        <FloatingLabelInput
          label="Confirm Password"
          type="password"
          value={formData.confirmPassword}
          onChange={(val) => {
            setFormData({ ...formData, confirmPassword: val });
            setErrors({ ...errors, confirmPassword: false });
          }}
          icon={``}
          className="w-full"
          error={errors.confirmPassword}
          errorMessage={
            !formData.confirmPassword
              ? 'Please confirm your password'
              : 'Passwords do not match'
          }
        />

        <div className="flex flex-col gap-1">
          <div className="flex items-start gap-[10px]">
            <button
              type="button"
              aria-label="Agree to terms"
              onClick={() => {
                setAgreeToTerms(!agreeToTerms);
                setTermsError(null);
              }}
              className={`mt-1 size-[20px] rounded-full flex items-center justify-center transition-colors border shrink-0 ${
                agreeToTerms
                  ? 'bg-[#8e2a2f] border-[#8e2a2f]'
                  : `bg-white ${termsError ? 'border-red-500' : 'border-[#eee]'}`
              }`}
            >
              {agreeToTerms && <span className="text-white text-[12px]">✓</span>}
            </button>
            <p className="font-['DM_Sans',sans-serif] font-normal leading-[21px] text-[#908e8e] text-[14px] tracking-[-0.1px]">
              By clicking Create account, I agree that I have read and accepted
              the{' '}
              <button
                type="button"
                onClick={() =>
                  setActiveCms({ code: 'TERMS_CONDITIONS', title: 'Terms of Use' })
                }
                className="text-[#8e2a2f] font-bold underline hover:no-underline"
              >
                Terms of Use
              </button>{' '}
              and{' '}
              <button
                type="button"
                onClick={() =>
                  setActiveCms({ code: 'PRIVACY_POLICY', title: 'Privacy Policy' })
                }
                className="text-[#8e2a2f] font-bold underline hover:no-underline"
              >
                Privacy Policy
              </button>
              .
            </p>
          </div>
          {termsError && (
            <p className="font-['DM_Sans',sans-serif] text-[12px] text-red-500 pl-[30px]">
              {termsError}
            </p>
          )}
        </div>

        <PrimaryButton
          onClick={handleSubmit}
          className="w-full py-4 mt-2"
          disabled={submitting}
        >
          {submitting ? 'Creating account…' : 'Create Account'}
        </PrimaryButton>
      </div>

      <CmsModal
        isOpen={!!activeCms}
        onClose={() => setActiveCms(null)}
        code={activeCms?.code ?? 'TERMS_CONDITIONS'}
        userType="PROFESSIONAL"
        fallbackTitle={activeCms?.title}
      />
    </div>
  );
}
